import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { ArrowRight, ChevronLeft, MapPin } from "lucide-react";
import { getQueueIcon } from "../lib/queueIcons";

/**
 * Public landing page reached by scanning the establishment-wide GLOBAL QR
 * code (advanced multi-queue mode). The visitor picks which queue they
 * want to join, then is redirected to the regular per-queue scan-landing
 * (`/q/{queueId}`) that already handles ticket creation, payment, etc.
 */
export default function GlobalQueueLanding() {
  const { commerceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ commerce: null, queues: [], loading: true, error: "" });

  useEffect(() => {
    const load = async () => {
      try {
        const [c, q] = await Promise.all([
          api.get(`/commerces/${commerceId}`),
          api.get(`/commerces/${commerceId}/queues`),
        ]);
        const visible = (q.data || []).filter((qq) => qq.displayOnPublic !== false);
        const finalQueues = visible.length ? visible : (q.data || []);
        // UX engagement (fork 19+) : si une SEULE file est en mesure de
        // recevoir des tickets (ouverte, non suspendue, pas en drain),
        // on saute la page intermédiaire et on emmène directement le
        // visiteur sur la fiche établissement avec cette file pré-
        // sélectionnée — exactement comme si elle avait été scannée via
        // son QR individuel. Plus rapide pour les petits commerces.
        const takeable = finalQueues.filter(
          (qq) => qq.isOpen && !qq.isSuspended && !qq.drainMode,
        );
        if (takeable.length === 1) {
          navigate(`/commerce/${commerceId}?queueId=${takeable[0].id}&from=qr`, { replace: true });
          return;
        }
        setData({ commerce: c.data, queues: finalQueues, loading: false, error: "" });
      } catch (e) {
        setData({ commerce: null, queues: [], loading: false, error: e?.response?.data?.detail || "Établissement introuvable" });
      }
    };
    load();
  }, [commerceId, navigate]);

  if (data.loading) {
    return <div className="min-h-screen flex items-center justify-center text-subtle">Chargement…</div>;
  }
  if (data.error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <p className="text-2xl font-display font-bold text-ink">{data.error}</p>
        <Link to="/" className="mt-4 text-brand underline">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <div className="container mx-auto max-w-2xl px-4 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-1 text-subtle hover:text-ink text-sm mb-4" data-testid="global-back-home">
          <ChevronLeft size={14} /> Accueil
        </Link>
        <p className="overline mb-2">Bienvenue chez</p>
        <h1
          className="font-display text-3xl sm:text-4xl font-black tracking-tight text-ink"
          data-testid="global-commerce-name"
        >
          {data.commerce?.name}
        </h1>
        {(data.commerce?.city || data.commerce?.address) && (
          <p className="mt-1 flex items-center gap-1.5 text-subtle text-sm">
            <MapPin size={13} /> {[data.commerce?.address, data.commerce?.city].filter(Boolean).join(", ")}
          </p>
        )}

        <h2 className="mt-8 font-display text-xl sm:text-2xl font-black uppercase tracking-tight text-ink">
          Choisissez votre file d'attente
        </h2>
        <p className="text-subtle text-sm mt-1">
          Sélectionnez la file qui correspond à votre besoin, puis prenez votre ticket.
        </p>

        <div className="mt-5 space-y-2.5" data-testid="global-queue-list">
          {data.queues.length === 0 && (
            <p className="text-subtle italic text-sm">Aucune file disponible pour le moment.</p>
          )}
          {data.queues.map((q) => {
            const QIcon = getQueueIcon(q.icon);
            const color = q.color || "#1B5BFF";
            const open = !!q.isOpen;
            const suspended = !!q.isSuspended;
            return (
              <Link
                key={q.id}
                to={`/commerce/${commerceId}?queueId=${q.id}&from=qr`}
                className="group relative flex items-center gap-3 rounded-lg border-2 border-border bg-white hover:border-ink p-4 transition-colors"
                style={{ borderColor: color }}
                data-testid={`global-queue-${q.id}`}
              >
                <span
                  aria-hidden="true"
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full shrink-0"
                  style={{ backgroundColor: `${color}1A`, color }}
                >
                  {QIcon ? <QIcon size={22} /> : <span className="font-display font-black text-lg">{q.letter || q.name?.[0]?.toUpperCase() || "?"}</span>}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-black uppercase tracking-tight text-ink truncate">{q.name}</p>
                  <p className="text-xs text-subtle mt-0.5">
                    {suspended
                      ? "Suspendue, veuillez patienter"
                      : open
                      ? `${q.waitingCount ?? 0} visiteur${(q.waitingCount ?? 0) > 1 ? "s" : ""} en attente`
                      : "Fermée"}
                  </p>
                </div>
                <ArrowRight size={20} className="text-subtle group-hover:text-ink shrink-0" />
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
